import { createContext, useContext, type ReactNode } from 'react';

/**
 * ComponentDataMap - A map of component IDs to resolved or deferred loader data
 * Used to pass component-specific data from page loaders down to individual components
 */
export type ComponentDataMap = Record<string, unknown>;

const ComponentDataContext = createContext<ComponentDataMap | undefined>(undefined);

/**
 * ComponentDataProvider - Provides component data context at the page level
 *
 * This provider is created once at the page-level region and makes the componentData
 * map available to all nested components via hooks. It prevents the need to thread
 * data through props in nested layout components (like Grid, Carousel, etc.).
 *
 * @example
 * ```tsx
 * // In Region component (page mode)
 * <ComponentDataProvider value={pageComponentData}>
 *   <RegionWrapper>
 *     {components.map(comp => <Component component={comp} />)}
 *   </RegionWrapper>
 * </ComponentDataProvider>
 * ```
 */
export function ComponentDataProvider({ children, value }: { children: ReactNode; value: ComponentDataMap }) {
    return <ComponentDataContext.Provider value={value}>{children}</ComponentDataContext.Provider>;
}

/**
 * useComponentData - Access the full componentData map from context
 *
 * Returns the complete map of component IDs to data values. Primarily used
 * internally by Region to check if context already exists (to avoid nesting providers).
 *
 * @returns ComponentDataMap if within provider, undefined otherwise
 *
 * @example
 * ```tsx
 * const existingData = useComponentData();
 * if (!existingData) {
 *   // Create new provider
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useComponentData(): ComponentDataMap | undefined {
    return useContext(ComponentDataContext);
}

/**
 * useComponentDataById - Get a specific component's data by ID
 *
 * Retrieves resolved data or a deferred data promise from the context.
 * Used by the Component wrapper to fetch component-specific data for rendering.
 *
 * @param componentId - The unique ID of the component
 * @returns Component data, a deferred promise, or undefined if not found/no context
 *
 * @example
 * ```tsx
 * // In Component wrapper
 * const dataPromise = useComponentDataById(component.id);
 * return (
 *   <Await resolve={dataPromise}>
 *     {(data) => <DynamicComponent data={data} />}
 *   </Await>
 * );
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useComponentDataById(componentId: string): unknown {
    const componentData = useContext(ComponentDataContext);
    return componentData?.[componentId];
}
